/**
 * Discipline helpers (Phase 20A). Every discipline, provider type, PA category and visit service
 * comes from the generated registry (./disciplineRegistry.ts, written by the backend's
 * scripts/tools/generate-discipline-registry.mjs). Do not add discipline lists anywhere else.
 *
 * This file adds the UI layer on top: one colour palette for every screen (D-12) and the
 * lookups components need.
 */

import {
  DISCIPLINE_ENTRIES,
  EVALUATION_VISIT_TYPES,
  PA_CATEGORY_ENTRIES,
  PROVIDER_TYPE_ENTRIES,
  SERVICE_ENTRIES,
  DisciplineEntry,
  PaCategoryEntry,
  ProviderTypeEntry,
} from './disciplineRegistry';

type Entry = (typeof DISCIPLINE_ENTRIES)[number];
/** Codes of disciplines that are live today (RN, PT, OT, ST, MSW, HHA). */
export type ActiveDisciplineCode = Extract<Entry, { status: 'active' }>['code'];
/** PT, OT, ST: the active therapy disciplines. */
export type TherapyDisciplineCode = Extract<Entry, { status: 'active'; therapy: true }>['code'];
/** Codes of provider types that are live today. */
export type ActiveProviderTypeCode = Extract<(typeof PROVIDER_TYPE_ENTRIES)[number], { status: 'active' }>['code'];

export interface DisciplineColors {
  bgColor: string;
  textColor: string;
  borderColor: string;
  /** Left border of a schedule card */
  accentBorder: string;
  /** Small dot or solid chip */
  dot: string;
  /** Very light background */
  light: string;
  /** Pill: light background, coloured text and border */
  pill: string;
  /** Progress-bar fill, and a stronger one for the selected state */
  bar: string;
  barStrong: string;
  /** Dark text on a light background */
  label: string;
  /** 500 and 700 borders (hover and selected tab) */
  midBorder: string;
  strongBorder: string;
  /** Softer left border: a limited provider type of the discipline (LVN shifts) */
  accentBorderSoft: string;
}

/**
 * Tailwind classes per registry colour. Written out in full so Tailwind keeps them in the build.
 * This is a palette, not a discipline list: disciplines pick a colour in the registry.
 */
const PALETTE: Record<string, DisciplineColors> = {
  sky: {
    bgColor: 'bg-sky-100', textColor: 'text-sky-800', borderColor: 'border-sky-200', accentBorder: 'border-l-sky-500', dot: 'bg-sky-500',
    light: 'bg-sky-50', pill: 'bg-sky-50 text-sky-700 border border-sky-200', bar: 'bg-sky-500', barStrong: 'bg-sky-600',
    label: 'text-sky-700', midBorder: 'border-sky-500', strongBorder: 'border-sky-700',
    accentBorderSoft: 'border-l-sky-300',
  },
  emerald: {
    bgColor: 'bg-emerald-100', textColor: 'text-emerald-800', borderColor: 'border-emerald-200', accentBorder: 'border-l-emerald-500', dot: 'bg-emerald-500',
    light: 'bg-emerald-50', pill: 'bg-emerald-50 text-emerald-700 border border-emerald-200', bar: 'bg-emerald-500', barStrong: 'bg-emerald-600',
    label: 'text-emerald-700', midBorder: 'border-emerald-500', strongBorder: 'border-emerald-700',
    accentBorderSoft: 'border-l-emerald-300',
  },
  violet: {
    bgColor: 'bg-violet-100', textColor: 'text-violet-800', borderColor: 'border-violet-200', accentBorder: 'border-l-violet-500', dot: 'bg-violet-500',
    light: 'bg-violet-50', pill: 'bg-violet-50 text-violet-700 border border-violet-200', bar: 'bg-violet-500', barStrong: 'bg-violet-600',
    label: 'text-violet-700', midBorder: 'border-violet-500', strongBorder: 'border-violet-700',
    accentBorderSoft: 'border-l-violet-300',
  },
  amber: {
    bgColor: 'bg-amber-100', textColor: 'text-amber-800', borderColor: 'border-amber-200', accentBorder: 'border-l-amber-500', dot: 'bg-amber-500',
    light: 'bg-amber-50', pill: 'bg-amber-50 text-amber-700 border border-amber-200', bar: 'bg-amber-500', barStrong: 'bg-amber-600',
    label: 'text-amber-700', midBorder: 'border-amber-500', strongBorder: 'border-amber-700',
    accentBorderSoft: 'border-l-amber-300',
  },
  rose: {
    bgColor: 'bg-rose-100', textColor: 'text-rose-800', borderColor: 'border-rose-200', accentBorder: 'border-l-rose-500', dot: 'bg-rose-500',
    light: 'bg-rose-50', pill: 'bg-rose-50 text-rose-700 border border-rose-200', bar: 'bg-rose-500', barStrong: 'bg-rose-600',
    label: 'text-rose-700', midBorder: 'border-rose-500', strongBorder: 'border-rose-700',
    accentBorderSoft: 'border-l-rose-300',
  },
  lime: {
    bgColor: 'bg-lime-100', textColor: 'text-lime-800', borderColor: 'border-lime-200', accentBorder: 'border-l-lime-500', dot: 'bg-lime-500',
    light: 'bg-lime-50', pill: 'bg-lime-50 text-lime-700 border border-lime-200', bar: 'bg-lime-500', barStrong: 'bg-lime-600',
    label: 'text-lime-700', midBorder: 'border-lime-500', strongBorder: 'border-lime-700',
    accentBorderSoft: 'border-l-lime-300',
  },
  teal: {
    bgColor: 'bg-teal-100', textColor: 'text-teal-800', borderColor: 'border-teal-200', accentBorder: 'border-l-teal-500', dot: 'bg-teal-500',
    light: 'bg-teal-50', pill: 'bg-teal-50 text-teal-700 border border-teal-200', bar: 'bg-teal-500', barStrong: 'bg-teal-600',
    label: 'text-teal-700', midBorder: 'border-teal-500', strongBorder: 'border-teal-700',
    accentBorderSoft: 'border-l-teal-300',
  },
  cyan: {
    bgColor: 'bg-cyan-100', textColor: 'text-cyan-800', borderColor: 'border-cyan-200', accentBorder: 'border-l-cyan-500', dot: 'bg-cyan-500',
    light: 'bg-cyan-50', pill: 'bg-cyan-50 text-cyan-700 border border-cyan-200', bar: 'bg-cyan-500', barStrong: 'bg-cyan-600',
    label: 'text-cyan-700', midBorder: 'border-cyan-500', strongBorder: 'border-cyan-700',
    accentBorderSoft: 'border-l-cyan-300',
  },
  orange: {
    bgColor: 'bg-orange-100', textColor: 'text-orange-800', borderColor: 'border-orange-200', accentBorder: 'border-l-orange-500', dot: 'bg-orange-500',
    light: 'bg-orange-50', pill: 'bg-orange-50 text-orange-700 border border-orange-200', bar: 'bg-orange-500', barStrong: 'bg-orange-600',
    label: 'text-orange-700', midBorder: 'border-orange-500', strongBorder: 'border-orange-700',
    accentBorderSoft: 'border-l-orange-300',
  },
  fuchsia: {
    bgColor: 'bg-fuchsia-100', textColor: 'text-fuchsia-800', borderColor: 'border-fuchsia-200', accentBorder: 'border-l-fuchsia-500', dot: 'bg-fuchsia-500',
    light: 'bg-fuchsia-50', pill: 'bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200', bar: 'bg-fuchsia-500', barStrong: 'bg-fuchsia-600',
    label: 'text-fuchsia-700', midBorder: 'border-fuchsia-500', strongBorder: 'border-fuchsia-700',
    accentBorderSoft: 'border-l-fuchsia-300',
  },
};
const GRAY: DisciplineColors = {
  bgColor: 'bg-gray-100', textColor: 'text-gray-800', borderColor: 'border-gray-200', accentBorder: 'border-l-gray-400', dot: 'bg-gray-400',
  light: 'bg-gray-50', pill: 'bg-gray-50 text-gray-700 border border-gray-200', bar: 'bg-gray-400', barStrong: 'bg-gray-500',
  label: 'text-gray-700', midBorder: 'border-gray-400', strongBorder: 'border-gray-600',
  accentBorderSoft: 'border-l-gray-300',
};

const ALIASES: Record<string, string> = { LPN: 'LVN', OTA: 'COTA', SLP: 'ST', CNA: 'HHA', AIDE: 'HHA', NURSING: 'RN' };
const up = (v: string | null | undefined) => String(v ?? '').trim().toUpperCase();

interface Options { includePlanned?: boolean }
const live = (status: string, o: Options = {}) => o.includePlanned === true || status === 'active';

// ── Disciplines ──────────────────────────────────────────────────────────────

/** Registry entries, display order. */
export function disciplineEntries(o: Options = {}): DisciplineEntry[] {
  return (DISCIPLINE_ENTRIES as readonly DisciplineEntry[]).filter(d => live(d.status, o));
}

/** Discipline codes, display order (RN, PT, OT, ST, MSW, HHA today). */
export function disciplineCodes(o: Options = {}): string[] {
  return disciplineEntries(o).map(d => d.code);
}

export function getDisciplineEntry(code: string | null | undefined, o: Options = { includePlanned: true }): DisciplineEntry | null {
  const c = up(code);
  return disciplineEntries(o).find(d => d.code === c) || null;
}

/** Disciplines with the given record form (assessment, carePlan, cms485, formKeys). */
export function disciplinesWithForm(form: keyof DisciplineEntry['forms']): string[] {
  return disciplineEntries().filter(d => d.forms[form]).map(d => d.code);
}

/** PT, OT, ST. */
export function therapyDisciplines(): string[] {
  return disciplineEntries().filter(d => d.therapy).map(d => d.code);
}

/** Disciplines shown on the Overview only once ordered (MSW, HHA and the planned ones). */
export function optionalTrackDisciplines(): string[] {
  return disciplineEntries().filter(d => d.optionalTrack).map(d => d.code);
}

/** Short label ("Physical Therapy"); a provider type shows its discipline's label. Unknown → as given. */
export function disciplineLabel(code: string | null | undefined): string {
  const entry = getDisciplineEntry(code) || getDisciplineEntry(disciplineForProviderType(code));
  return entry?.label || String(code ?? '');
}

/** Long label ("Private Duty Nursing"). */
export function disciplineLongLabel(code: string | null | undefined): string {
  return getDisciplineEntry(code)?.longLabel || disciplineLabel(code);
}

/** Referral "Requested service" option label ("PT (Physical Therapy)"). */
export function disciplineReferralLabel(code: string): string {
  return getDisciplineEntry(code)?.referralLabel || code;
}

/** Colour classes for a discipline or provider type (LVN uses nursing, PTA uses PT). */
export function getDisciplineColors(discipline: string | null | undefined): DisciplineColors {
  const entry = getDisciplineEntry(discipline) || getDisciplineEntry(disciplineForProviderType(discipline));
  return (entry && PALETTE[entry.color]) || GRAY;
}

// ── Provider types ───────────────────────────────────────────────────────────

export function providerTypeEntries(o: Options = {}): ProviderTypeEntry[] {
  return (PROVIDER_TYPE_ENTRIES as readonly ProviderTypeEntry[]).filter(p => live(p.status, o));
}

/** Provider type codes (RN, LVN, PT, PTA, OT, COTA, ST, SLPA, MSW, HHA today). */
export function providerTypeCodes(o: Options = {}): string[] {
  return providerTypeEntries(o).map(p => p.code);
}

export function getProviderTypeEntry(code: string | null | undefined, o: Options = { includePlanned: true }): ProviderTypeEntry | null {
  const raw = up(code);
  const all = providerTypeEntries(o);
  return all.find(p => p.code === raw) || all.find(p => p.code === ALIASES[raw]) || null;
}

/** Discipline a provider type fills (LVN → RN, PTA → PT); a discipline code maps to itself. */
export function disciplineForProviderType(code: string | null | undefined): string | null {
  const p = getProviderTypeEntry(code);
  if (p) return p.discipline;
  return getDisciplineEntry(code) ? up(code) : null;
}

export function isAssistantProviderType(code: string | null | undefined): boolean {
  return getProviderTypeEntry(code)?.role === 'assistant';
}

/** Provider types that can fill a care-team slot of the discipline (licensed first). */
export function providerTypesForDiscipline(discipline: string): string[] {
  const d = up(discipline);
  return providerTypeEntries()
    .filter(p => p.discipline === d)
    .sort((a, b) => (a.role === b.role ? 0 : a.role === 'licensed' ? -1 : 1))
    .map(p => p.code);
}

/** Mirrors the backend rule: same discipline; LVNs and assistants never take evaluation visits. */
export function providerFitsDiscipline(providerType: string, discipline: string, visitType?: string): boolean {
  const p = getProviderTypeEntry(providerType);
  const d = up(discipline) === 'LVN' ? 'RN' : up(discipline);
  if (!p) return up(providerType) === d;
  if (p.discipline !== d) return false;
  const evaluation = (EVALUATION_VISIT_TYPES as readonly string[]).includes(up(visitType));
  return !(evaluation && (p.role === 'assistant' || p.limitedNursing === true));
}

// ── PA categories and services ───────────────────────────────────────────────

export function paCategoryEntries(o: Options = {}): PaCategoryEntry[] {
  return (PA_CATEGORY_ENTRIES as readonly PaCategoryEntry[]).filter(c => live(c.status, o));
}

export function getPaCategoryEntry(code: string | null | undefined): PaCategoryEntry | null {
  const c = String(code ?? '').trim().toLowerCase();
  return paCategoryEntries({ includePlanned: true }).find(x => x.code === c) || null;
}

/** PA category → visit service (pdn → PDN, sn_visit → SKILLED_NURSING_VISIT). */
export function paCategoryService(code: string | null | undefined): string | null {
  return getPaCategoryEntry(code)?.service || null;
}

/** PA category → discipline (pdn and sn_visit → RN). */
export function paCategoryDiscipline(code: string | null | undefined): string | null {
  return getPaCategoryEntry(code)?.discipline || null;
}

type ServiceRow = { code: string; label: string; discipline: string; evv: string; status: string };
const serviceRows = () => SERVICE_ENTRIES as readonly ServiceRow[];

/** Short label of a visit service ("Skilled nursing", "Aide"). */
export function serviceLabel(code: string | null | undefined): string {
  return serviceRows().find(s => s.code === up(code))?.label || String(code ?? '');
}

/** EVV class of a visit service; null when unknown. */
export function serviceEvvClass(code: string | null | undefined): 'REQUIRED' | 'OPTIONAL' | 'NOT_REQUIRED' | null {
  return (serviceRows().find(s => s.code === up(code))?.evv as 'REQUIRED' | 'OPTIONAL' | 'NOT_REQUIRED') || null;
}

/** Visit service a non-nursing discipline bills (PT → PT, HHA → HHA, ATTENDANT → PCS). */
export function serviceForDiscipline(discipline: string | null | undefined): string | null {
  const d = up(discipline);
  return serviceRows().find(s => s.discipline === d && s.code !== 'PDN' && s.code !== 'SKILLED_NURSING_VISIT')?.code || null;
}

/** Visit service codes for billing rows (PDN, SKILLED_NURSING_VISIT, PT, …). */
export function serviceCodes(o: Options = {}): string[] {
  return serviceRows().filter(s => live(s.status, o)).map(s => s.code);
}

// ── Compatibility exports (pre-20A names) ────────────────────────────────────

export interface DisciplineInfo {
  api: string;
  ui: string;
  uiEnum: string;
  color: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

/** `{ RN: { api, ui, color, … }, … }` for the active disciplines. */
export const DISCIPLINES: Readonly<Record<string, DisciplineInfo>> = Object.freeze(Object.fromEntries(
  disciplineEntries().map(d => {
    const colors = PALETTE[d.color] || GRAY;
    return [d.code, { api: d.code, ui: d.label, uiEnum: d.code, color: d.color, bgColor: colors.bgColor, textColor: colors.textColor, borderColor: colors.borderColor }];
  })
));

export type DisciplineKey = ActiveDisciplineCode;

/** API discipline value → display name. */
export const getDisciplineDisplay = (apiValue: string): string => disciplineLabel(apiValue);

/** Display name → API discipline value. */
export const getDisciplineApi = (uiValue: string): string => {
  const entry = disciplineEntries({ includePlanned: true }).find(d => d.label.toLowerCase() === uiValue.toLowerCase() || d.longLabel.toLowerCase() === uiValue.toLowerCase());
  return entry?.code ?? uiValue;
};

export const DISCIPLINE_KEYS = disciplineCodes() as DisciplineKey[];

export const DISCIPLINE_DISPLAY_NAMES = disciplineEntries().map(d => d.label);
